import React, { forwardRef } from 'react'
import { CloseIcon, DotsHorizontalIcon } from '@fourthwall/icons'
import type {
  TableBodyTdProps,
  TableCellItemPositionerProps,
  TableHeadThProps,
  TableUIProps,
  TableHeadProps,
  TableHeaderSelectionWrapperProps,
  TableBodyProps,
  TableBodyTrProps,
  TableHeaderActionsProps,
  TableHeaderActionsItemProps,
  TableRowActionsProps,
  TableRowActionsItemProps,
  TableRowXActionIconProps,
  TableRowSelectionProps,
  TableHeadTrProps,
} from './types'
import { Loader } from '../../common/Loader'
import { Icon } from '../../common/Icon'
import { Dropdown } from '../../common/Dropdown'
import { TableBodyProvider, useTableBody } from './contexts/TableBodyContext'

import * as S from './styled'

const TableUI = forwardRef<HTMLTableElement, TableUIProps>((props, ref) => {
  return (
    <S.Root
      ref={ref}
      role="table"
      $hoverable={props.hoverable}
      $minWidth={props.minWidth}
      {...props}
    >
      {props.children}
    </S.Root>
  )
})

const TableHead = ({ children }: TableHeadProps) => {
  return <S.TableHead>{children}</S.TableHead>
}

const TableHeadTr = ({ children }: TableHeadTrProps) => {
  return <S.TableHeadTr>{children}</S.TableHeadTr>
}

const TableHeaderSelectionWrapper = ({ children, visible }: TableHeaderSelectionWrapperProps) => {
  return (
    <S.TableHeaderSelectionWrapper $visible={visible}>{children}</S.TableHeaderSelectionWrapper>
  )
}

const TableHeadTh = ({
  children,
  colSpan,
  textAlign = 'center',
  width,
  position,
  verticalSpacing,
  horizontalSpacing,
}: TableHeadThProps) => {
  return (
    <S.TableHeadTh
      colSpan={colSpan}
      $textAlign={textAlign}
      $width={width}
      $position={position}
      $verticalSpacing={verticalSpacing}
      $horizontalSpacing={horizontalSpacing}
    >
      {children}
    </S.TableHeadTh>
  )
}

const TableBody = ({ children, isLoading }: TableBodyProps) => {
  return (
    <TableBodyProvider>
      <S.TableBody $isLoading={isLoading}>{children}</S.TableBody>
    </TableBodyProvider>
  )
}

const TableBodyTr = forwardRef<HTMLTableRowElement, TableBodyTrProps>(
  ({ children, isDragged, isSelected, ...props }, ref) => {
    const { setDropdownKey } = useTableBody()

    return (
      <S.TableBodyTr
        ref={ref}
        {...props}
        $isDragged={isDragged}
        $isSelected={isSelected}
        onMouseLeave={() => {
          setDropdownKey((key) => key + 1)
        }}
      >
        {children}
      </S.TableBodyTr>
    )
  },
)

const TableBodyTd = forwardRef<HTMLTableCellElement, TableBodyTdProps>(
  (
    {
      children,
      colSpan,
      alignContent,
      textAlign,
      width,
      height,
      borderTop,
      position,
      verticalSpacing,
      horizontalSpacing,
      verticalAlign,
      onClick,
      ...props
    }: TableBodyTdProps,
    ref,
  ) => {
    return (
      <S.TableBodyTd
        {...props}
        ref={ref}
        colSpan={colSpan}
        $alignContent={alignContent}
        $textAlign={textAlign}
        $width={width}
        $height={height}
        $borderTop={borderTop}
        $position={position}
        $verticalAlign={verticalAlign}
        $verticalSpacing={verticalSpacing}
        $horizontalSpacing={horizontalSpacing}
        onClick={onClick}
      >
        {children}
      </S.TableBodyTd>
    )
  },
)

const TableCellItemPositioner = ({
  children,
  position,
  width,
  height,
}: TableCellItemPositionerProps) => {
  return (
    <S.TableCellItemPositioner $position={position} $width={width} $height={height}>
      {children}
    </S.TableCellItemPositioner>
  )
}

const TableLoader = () => {
  return (
    <S.TableLoaderWrapper>
      <Loader size="large" />
    </S.TableLoaderWrapper>
  )
}

export const TableHeaderActions = ({ children }: TableHeaderActionsProps) => {
  return <S.TableHeaderActions>{children}</S.TableHeaderActions>
}

export const TableHeaderActionsItem = ({ children, onClick }: TableHeaderActionsItemProps) => {
  return <S.TableHeaderActionsItem onClick={onClick}>{children}</S.TableHeaderActionsItem>
}

export const TableRowActions = ({ children }: TableRowActionsProps) => {
  const { dropdownKey } = useTableBody()

  return (
    <S.TableRowActionsWrapper>
      <Dropdown
        key={dropdownKey}
        content={children}
        position="bottom-end"
        optionsVerticalOffset={0}
        optionsHorizontalOffset={0}
      >
        <S.TableRowActionsContainer type="button">
          <div>
            <Icon component={DotsHorizontalIcon} height={16} />
          </div>
        </S.TableRowActionsContainer>
      </Dropdown>
    </S.TableRowActionsWrapper>
  )
}

export const TableRowActionsItem = ({ label, onClick }: TableRowActionsItemProps) => {
  return <Dropdown.Item label={label} onClick={onClick} />
}

export const TableRowXActionIcon = ({ onClick }: TableRowXActionIconProps) => {
  return (
    <S.TableRowActionsWrapper>
      <S.TableRowActionsContainer type="button" onClick={onClick}>
        <Icon component={CloseIcon} height={16} />
      </S.TableRowActionsContainer>
    </S.TableRowActionsWrapper>
  )
}

export const TableRowSelection = ({ children, visible }: TableRowSelectionProps) => {
  return <S.TableRowSelectionWrapper $visible={visible}>{children}</S.TableRowSelectionWrapper>
}

const TableHeaderHeading = S.TableHeaderActionsHeading
const TableHeaderRoot = S.TableHeaderRoot
const TableHeaderActionsHeading = S.TableHeaderActionsHeading
const TableHeaderActionsItems = S.TableHeaderActionsItems

TableUI.displayName = 'TableUI'

export {
  TableUI as Root,
  TableHead as Head,
  TableHeadTr as HeadTr,
  TableHeadTh as HeadTh,
  TableBody as Body,
  TableBodyTd as BodyTd,
  TableBodyTr as BodyTr,
  TableCellItemPositioner as CellItemPositioner,
  TableLoader as Loader,
  TableHeaderHeading as HeaderHeading,
  TableHeaderRoot as HeaderRoot,
  TableHeaderSelectionWrapper as HeaderSelectionWrapper,
  TableHeaderActions as HeaderActions,
  TableHeaderActionsItems as HeaderActionsItems,
  TableHeaderActionsItem as HeaderActionsItem,
  TableHeaderActionsHeading as HeaderActionsHeading,
  TableRowActions as RowActions,
  TableRowActionsItem as RowActionsItem,
  TableRowXActionIcon as RowXActionIcon,
  TableRowSelection as RowSelection,
}
